import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Send, Loader2, ShieldOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { SmsBubble, markThreadRead } from "@/components/helm/SmsInbox";

// Household "Text" action: the same conversation as the main inbox (same server-side thread id),
// pre-addressed to the household's number and sent from the assigned rep's business number.
export const HouseholdTextButton = ({ householdId }) => {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (open) return;
    api.get(`/sms/household/${householdId}/context`)
      .then(({ data }) => setUnread((data.messages || []).filter((m) => m.direction === "inbound" && m.unread).length))
      .catch(() => setUnread(0));
  }, [householdId, open]);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="household-text-btn"
        className="bg-[#1E3E62] hover:bg-[#162f4d] text-white rounded-full px-4">
        <MessageSquare className="w-4 h-4 mr-1.5" /> Text
        {unread > 0 && <span className="ml-1.5 text-[10px] font-bold rounded-full bg-[#B91C1C] px-1.5" data-testid="household-text-unread">{unread}</span>}
      </Button>
      {open && <HouseholdTextDialog householdId={householdId} onClose={() => setOpen(false)} />}
    </>
  );
};

const HouseholdTextDialog = ({ householdId, onClose }) => {
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/sms/household/${householdId}/context`); setCtx(data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || "Couldn't load this conversation."); }
  }, [householdId]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const unread = (ctx?.messages || []).filter((m) => m.direction === "inbound" && m.unread).length;
  useEffect(() => { if (ctx?.thread_key && unread > 0) markThreadRead(ctx.thread_key).then(load); }, [ctx?.thread_key, unread, load]);

  const send = async () => {
    if (!body.trim()) { toast.error("Enter a message."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/sms/send", { to: ctx.contact_phone, body, household_id: householdId });
      toast.success(data.simulated ? "Queued (simulated — texting is not live yet)." : "Message sent.");
      setBody(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send this text."); }
    finally { setBusy(false); }
  };

  const thread = (ctx?.messages || []).slice().reverse();
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)]" data-testid="household-text-dialog">
        <DialogHeader><DialogTitle>Text this household</DialogTitle></DialogHeader>
        {err && <p className="text-sm text-[#B91C1C]" data-testid="household-text-error">{err}</p>}
        {!ctx && !err && <p className="text-xs text-[#9CA3AF]">Loading…</p>}
        {ctx && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md bg-[#F8FAFC] border p-2"><p className="text-[#6B7280]">To (household)</p>
                <p className="font-semibold text-[#0B192C]" data-testid="household-text-to">{ctx.contact_phone || "No mobile on file"}</p></div>
              <div className="rounded-md bg-[#F8FAFC] border p-2"><p className="text-[#6B7280]">From (assigned rep)</p>
                <p className="font-semibold text-[#0B192C]" data-testid="household-text-from">{ctx.sender_number || "No business number"}</p></div>
            </div>
            {ctx.contact_phone && (
              <p className={`text-[11px] flex items-center gap-1.5 ${ctx.opted_in || (ctx.consent_basis && !ctx.opted_out) ? "text-[#047857]" : "text-[#B91C1C]"}`} data-testid="household-text-consent">
                {ctx.opted_in || (ctx.consent_basis && !ctx.opted_out) ? <ShieldCheck className="w-3 h-3" /> : <ShieldOff className="w-3 h-3" />}
                {ctx.opted_in ? "Opted in to texts" : ctx.opted_out ? "Opted out (STOP)" : ctx.consent_basis ? "Consent on file (SBA lead)" : "No logged texting consent"}
              </p>
            )}
            {!ctx.effective_live && <p className="text-[11px] text-[#9A3412]" data-testid="household-text-sim">Texting is not live in this environment — sends are recorded as a simulation.</p>}
            <div className="max-h-72 overflow-y-auto space-y-2 rounded-md border p-3 bg-white" data-testid="household-text-thread">
              {thread.length ? thread.map((m) => <SmsBubble key={m.id} m={m} />)
                : <p className="text-xs text-[#9CA3AF]">No messages in this conversation yet.</p>}
            </div>
            {ctx.can_text ? (
              <div className="flex gap-2">
                <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className="h-9 text-sm"
                  data-testid="household-text-body" onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }} />
                <Button onClick={send} disabled={busy} className="h-9 bg-[#0B192C] text-white" data-testid="household-text-send">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send</>}
                </Button>
              </div>
            ) : (
              <p className="text-xs rounded-md bg-[#FDF3F3] text-[#991B1B] p-2" data-testid="household-text-unavailable">
                Texting unavailable: {ctx.reason}
              </p>
            )}
            {ctx.thread_key && (
              <Link to={`/agent/messages?thread=${encodeURIComponent(ctx.thread_key)}`} className="text-[11px] text-[#1E3E62] underline" data-testid="household-text-open-inbox">
                Open this conversation in Messages
              </Link>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
